import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import Button from "components/Button";
import { ContentArea as BaseContextArea, Board, FooterArea, FormRow, TitleArea } from "components/LayoutParts";
import RadioButton from "components/RadioButton";
import UserModal, { UserModalConfig } from "components/UserModal";
import color from "constants/color";
import font from "constants/font";
import { useFindSystemNotificationById } from "pages/AtPort/api/useFindSystemNotificationById";
import { useUpdateSystemNotification } from "pages/AtPort/api/useUpdateSystemNotification";
import NotificationCategorySwitcher from "pages/AtPort/component/NotificationCategorySwitcher";
import SystemNotificationDuration from "pages/AtPort/component/SystemNotificationDuration";
import {
  CONFIRM_MODAL_CONFIG,
  FAILED_MODAL_CONFIG,
  INITIAL_MODAL_CONFIG,
  SUCCEED_MODAL_CONFIG,
} from "pages/AtPort/modalTemplate";
import { dateFormat } from "utils/dateTimeUtils";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
  flex: 1;
  justify-content: space-between;
`;

const ContentArea = styled(BaseContextArea)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 32px 48px;
  color: ${color.text.black};
`;

const HeaderArea = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  justify-content: space-between;
  font-size: ${font.size12};
  gap: 16px;
  border-bottom: solid 1px ${color.form.border};
  padding-bottom: 14px;
`;

const NotificationTitleArea = styled.div`
  font-size: ${font.size20};
  font-weight: bold;
`;

const HeaderItem = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: end;
`;

const MessageArea = styled.div`
  white-space: pre-wrap;
  color: ${color.text.black};
`;

const HomeDisplaySettingArea = styled.div`
  display: flex;
  flex: 1;
  border-top: solid 1px ${color.form.border};
`;

const Label = styled.div``;

const FormLabel = styled.div`
  font-size: ${font.size14};
  width: 140px;
`;

const Value = styled.div``;

const CancelWrap = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;
const SaveWrap = styled(CancelWrap)`
  align-items: center;
  justify-content: flex-end;
  flex: 1;
`;

// パーツ定義
const EditSystemNotification = () => {
  const [homeDisplay, setHomeDisplay] = useState(false);
  const [modalConfig, setModalConfig] = useState<UserModalConfig>(INITIAL_MODAL_CONFIG);
  const [isVisible, setIsVisible] = useState(false);

  const navigate = useNavigate();
  const { id: notificationId } = useParams() as { id: string };
  const { data: notification } = useFindSystemNotificationById(notificationId ?? "");
  const { update } = useUpdateSystemNotification();

  const isChangedHomeDisplay = homeDisplay !== notification?.homeDisplay;
  const isChanged = isChangedHomeDisplay;
  const canSubmit = isChanged;

  const onSubmit = useCallback(async () => {
    const response = await update(notificationId, {
      homeDisplay,
    }).catch((e) => {
      setModalConfig(FAILED_MODAL_CONFIG({ onClose: () => setIsVisible(false) }));
      setIsVisible(true);
      throw e;
    });

    if (response.status === 200) {
      setModalConfig(
        SUCCEED_MODAL_CONFIG({
          message: "完了しました",
          onClose: () => navigate(`/at_port/system_notification`),
        })
      );
      setIsVisible(true);
    }
  }, [update, notificationId, homeDisplay, setModalConfig, setIsVisible, navigate]);

  const handleOpenSubmitModal = useCallback(() => {
    setModalConfig(
      CONFIRM_MODAL_CONFIG({
        negative: () => setIsVisible(false),
        positive: onSubmit,
      })
    );
    setIsVisible(true);
  }, [onSubmit, setModalConfig, setIsVisible]);

  const setUp = useCallback(() => {
    if (notification) {
      setHomeDisplay(notification.homeDisplay);
    }
  }, [notification]);

  useEffect(() => {
    setUp();
  }, [setUp]);

  return (
    <Board>
      <TitleArea>
        <Breadcrumb
          currentPageName={`お知らせを編集`}
          breadcrumbItems={[
            {
              pageName: `お知らせ`,
              onClick: () => navigate(`/at_port/system_notification`),
            },
          ]}
        />
      </TitleArea>
      {notification && (
        <Wrap>
          <ContentArea>
            <HeaderArea>
              <NotificationCategorySwitcher category={notification.category} />
              <HeaderItem>
                <Label>送信日</Label>
                <Value>{dateFormat(notification.createdAt, "YYYY/MM/DD HH:mm")}</Value>
              </HeaderItem>
            </HeaderArea>
            <NotificationTitleArea>{notification.title}</NotificationTitleArea>
            <MessageArea>{notification.content}</MessageArea>
            {notification.startAt && notification.endAt && (
              <SystemNotificationDuration
                start={notification.startAt}
                end={notification.endAt}
                title="メンテナンス期間"
              />
            )}
            <HomeDisplaySettingArea>
              <FormRow>
                <FormLabel>ホーム画面に表示</FormLabel>
                <RadioButton
                  id="home_display_edit_btn"
                  value={homeDisplay ? 1 : 0}
                  choices={[
                    {
                      label: "表示する",
                      value: 1,
                    },
                    {
                      label: "表示しない",
                      value: 0,
                    },
                  ]}
                  onChange={(v) => setHomeDisplay(Number(v.target.value) === 1)}
                />
              </FormRow>
            </HomeDisplaySettingArea>
          </ContentArea>
          <FooterArea>
            <CancelWrap>
              <Button
                type="secondary"
                onClick={() => navigate("/at_port/system_notification")}
                label="キャンセル"
                width="160px"
              />
            </CancelWrap>
            <SaveWrap>
              <Button
                type="primary"
                onClick={handleOpenSubmitModal}
                label="変更内容を保存"
                disabled={!canSubmit}
                width="160px"
              />
            </SaveWrap>
          </FooterArea>
        </Wrap>
      )}
      <UserModal isVisible={isVisible} config={modalConfig} />
    </Board>
  );
};

export default EditSystemNotification;
